/**
 * useVoicePrompts Hook
 * 
 * React hook for loading voice conversation prompts from the LLM Query Manager (Firestore).
 * Supports Position 1/2/3 hierarchy - prompts are automatically merged with highest position winning.
 * 
 * Usage:
 *   const { prompts, isLoading, error, isAvailable } = useVoicePrompts();
 *   
 *   if (isLoading) return <LoadingSpinner />;
 *   if (error) return <ServiceUnavailable message={error} />;
 *   
 *   // Use prompts - already merged from all applicable positions
 *   askQuestion(prompts.opening);
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@healthdesk/shared-hooks';
import {
  fetchVoiceConversationPrompts,
  fetchVoiceConversationPromptsForBase,
  resolveSc4VoicePromptsBaseQueryId,
  resolveVoicePromptsBaseQueryIdWithFallback,
  clearPromptsCache,
  DEFAULT_VOICE_PROMPTS,
} from 'services/symptomCheck/voiceConversationPromptsService';
import { shouldSuppressProxyForFamilyHistory } from './helpers';

const PROMPTS_FETCH_TIMEOUT_MS = 45000;

/**
 * Hook to load and access voice conversation prompts
 * Automatically loads prompts based on user's region/locality for proper position hierarchy
 * 
 * @param {Object} options - Optional overrides
 * @param {string} options.region - Override user's region
 * @param {string} options.locality - Override user's locality
 * @param {'default'|'sc4'} [options.voicePromptsVersion='default'] - Selects base query resolution path
 * @param {string} [options.voicePromptsQueryId] - Override base Firestore query id (e.g. /scmap2 fork)
 * @returns {Object} { prompts, isLoading, error, isAvailable, retry }
 */
export function useVoicePrompts(options = {}) {
  const voicePromptsVersion = options.voicePromptsVersion ?? 'default';
  const voicePromptsQueryId = options.voicePromptsQueryId ?? null;
  const { userData } = useAuth();
  const [prompts, setPrompts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadGenerationRef = useRef(0);

  // Primitives only (avoid useMemo on whole userData — new object refs each Auth snapshot).
  const userRegion =
    options.region ?? userData?.scope?.regions?.[0] ?? userData?.region ?? null;
  const userLocality =
    options.locality ?? userData?.scope?.localities?.[0] ?? userData?.locality ?? null;

  const loadPrompts = useCallback(async () => {
    const gen = ++loadGenerationRef.current;
    setIsLoading(true);
    setError(null);

    const timeoutError = () =>
      new Error(
        'Timed out loading symptom checker. Check your connection and try again.'
      );

    try {
      const loadedPrompts = await Promise.race([
        (async () => {
          if (voicePromptsVersion === 'sc4') {
            const baseQueryId = await resolveSc4VoicePromptsBaseQueryId();
            return fetchVoiceConversationPromptsForBase(baseQueryId, {
              region: userRegion,
              locality: userLocality,
            });
          }
          if (voicePromptsQueryId) {
            const baseQueryId = await resolveVoicePromptsBaseQueryIdWithFallback(
              voicePromptsQueryId
            );
            return fetchVoiceConversationPromptsForBase(baseQueryId, {
              region: userRegion,
              locality: userLocality,
            });
          }
          return fetchVoiceConversationPrompts({
            region: userRegion,
            locality: userLocality,
          });
        })(),
        new Promise((_, reject) =>
          setTimeout(() => reject(timeoutError()), PROMPTS_FETCH_TIMEOUT_MS)
        ),
      ]);
      if (gen !== loadGenerationRef.current) return;
      setPrompts(loadedPrompts);
      setError(null);
    } catch (err) {
      if (gen !== loadGenerationRef.current) return;
      console.error('[useVoicePrompts] Failed to load prompts:', err);
      setError(err.message || 'Unable to load symptom checker. Please try again later.');
      setPrompts(null);
    } finally {
      if (gen === loadGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, [userRegion, userLocality, voicePromptsVersion, voicePromptsQueryId]);

  // Load prompts on mount and when region/locality changes
  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  // Retry function for user-initiated retries
  const retry = useCallback(() => {
    clearPromptsCache();
    loadPrompts();
  }, [loadPrompts]);

  return {
    prompts,
    isLoading,
    error,
    isAvailable: !!prompts && !error,
    retry,
    // Include context for debugging
    context: {
      region: userRegion,
      locality: userLocality,
    },
  };
}

/**
 * Helper to get prerequisite questions based on proxy mode
 * 
 * @param {Object} prompts - Loaded prompts object
 * @param {boolean} isProxyMode - Whether in proxy mode
 * @returns {Array<string>} Array of prerequisite questions
 */
export function getPrerequisiteQuestions(prompts, isProxyMode = false) {
  if (!prompts) return [];
  return isProxyMode 
    ? prompts.proxyPrerequisiteQuestions || []
    : prompts.prerequisiteQuestions || [];
}

/**
 * Helper to get prerequisite indices based on proxy mode
 * 
 * @param {Object} prompts - Loaded prompts object
 * @param {boolean} isProxyMode - Whether in proxy mode
 * @returns {Object} Indices object
 */
export function getPrerequisiteIndices(prompts, isProxyMode = false) {
  if (!prompts) {
    return { demographics: 0, durationChief: 1, associatedSymptoms: 2, durationAssociated: 3 };
  }
  return isProxyMode 
    ? prompts.proxyPrerequisiteIndices || {}
    : prompts.prerequisiteIndices || {};
}

/**
 * Helper to check if a pattern indicates proxy mode
 * 
 * @param {Object} prompts - Loaded prompts object
 * @param {string} text - Text to check
 * @returns {boolean} True if proxy indicators found
 */
export function detectProxyMode(prompts, text) {
  if (!prompts || !prompts.proxyIndicators || !text) return false;
  if (shouldSuppressProxyForFamilyHistory(text)) return false;
  const lowerText = text.toLowerCase();
  return prompts.proxyIndicators.some(pattern => lowerText.includes(pattern.toLowerCase()));
}

// Export default prompts for initialization/reference
export { DEFAULT_VOICE_PROMPTS };
